import React, { useEffect, useState } from "react";
import { api, inr, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CaretUp, CaretDown, TrendUp, WarningCircle, Vault, Bank, Storefront, MapPin } from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { Link } from "react-router-dom";

const KPI = ({ label, value, delta, tone = "info", testid }) => (
  <div className="kpi" data-testid={testid}>
    <div className="eyebrow">{label}</div>
    <div className="val">{value}</div>
    {delta !== undefined && (
      <div className={`text-[12px] mt-2 font-mono ${delta >= 0 ? "delta-up" : "delta-down"} flex items-center gap-1`}>
        {delta >= 0 ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
        {Math.abs(delta).toFixed(2)}%
      </div>
    )}
  </div>
);

export default function Dashboard() {
  const { activeCompanyId, activeStoreId, user } = useAuth();
  const [data, setData] = useState(null);
  const [cash, setCash] = useState(null);
  const [storeBreakdown, setStoreBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdminOrManager = ["admin", "manager", "regional_manager"].includes(user?.role);
  const lockedToStore = Boolean(user?.store_id) &&
    (user?.role === "manager" || user?.role === "employee");
  const viewingAllStores = !lockedToStore && (!activeStoreId || activeStoreId === "all");

  useEffect(() => {
    if (!activeCompanyId) return;
    setLoading(true);
    const requests = [
      api.get("/dashboard/overview"),
      api.get("/cashbook/summary").catch(() => ({ data: null })),
    ];
    // Only load per-store breakdown for admins looking at "All Stores"
    if (isAdminOrManager && viewingAllStores) {
      requests.push(api.get("/stores/summary").catch(() => ({ data: { stores: [] } })));
    }
    Promise.all(requests)
      .then((results) => {
        setData(results[0].data);
        setCash(results[1].data);
        if (results[2]) setStoreBreakdown(results[2].data);
      })
      .finally(() => setLoading(false));
  }, [activeCompanyId, activeStoreId, isAdminOrManager, viewingAllStores]);

  if (loading || !data) {
    return <div className="page-shell text-sm text-muted-foreground">Loading dashboard…</div>;
  }

  const k = data.kpis;

  const storeCount = storeBreakdown?.stores?.length || 0;
  const totalStoreSales = storeCount > 0
    ? storeBreakdown.stores.reduce((sum, s) => sum + (s.total_sales || 0), 0) : 0;
  const totalStoreOutstanding = storeCount > 0
    ? storeBreakdown.stores.reduce((sum, s) => sum + (s.outstanding || 0), 0) : 0;
  const totalStoreTempPending = storeCount > 0
    ? storeBreakdown.stores.reduce((sum, s) => sum + (s.temp_pending || 0), 0) : 0;

  return (
    <div className="page-shell space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Business Overview</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Hello, {user?.name?.split(" ")[0] || "there"}.</h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time snapshot of your business performance.</p>
        </div>
        {isAdminOrManager && viewingAllStores && storeCount > 0 && (
          <div
            className="rounded-md border border-primary/30 bg-primary/5 px-4 py-2.5 flex items-center gap-2 text-[12px]"
            data-testid="dash-consolidated-badge"
          >
            <Storefront size={18} weight="fill" className="text-primary" />
            <div>
              <div className="font-heading font-semibold text-[13px] text-primary uppercase tracking-wider">
                All {storeCount} store{storeCount > 1 ? "s" : ""} · consolidated
              </div>
              <div className="text-muted-foreground text-[11px] leading-tight">
                Every KPI below sums every store together — one unified accounting view.
              </div>
            </div>
          </div>
        )}
        {isAdminOrManager && !viewingAllStores && (
          <div className="rounded-md border border-amber-400/40 bg-amber-50 px-4 py-2 text-[12px] text-amber-800"
               data-testid="dash-store-badge">
            <b>Single store view.</b> Switch to <i>All Stores</i> in the top bar for the consolidated total.
          </div>
        )}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {user?.role === "admin" && k.fy_turnover != null && (
          <KPI label={`Turnover (${k.fy_label})`} value={inr(k.fy_turnover)} testid="kpi-fy-turnover" />
        )}
        <KPI label="Sales (This Month)" value={inr(k.total_sales_month)} testid="kpi-sales-month" />
        <KPI label="Purchases (This Month)" value={inr(k.total_purchases_month)} testid="kpi-purchases-month" />
        <KPI label="Profit (This Month)" value={inr(k.profit_month)} testid="kpi-profit-month" />
        <KPI label="Bank Balance" value={inr(k.bank_upi_wallet)} testid="kpi-bank-upi-wallet" />
        <KPI label="Stock Value" value={inr(k.stock_value)} testid="kpi-stock-value" />
        <KPI label="Receivables" value={inr(k.outstanding_receivables)} testid="kpi-receivables" />
        <KPI label="Payables" value={inr(k.outstanding_payables)} testid="kpi-payables" />
        <KPI label="GST Payable" value={inr(data.gst.total)} testid="kpi-gst" />
        <KPI label="Customers · Products" value={`${k.customers} · ${k.products}`} testid="kpi-counts" />
      </div>

      {/* Per-Store breakdown — admins / managers on "All Stores" only */}
      {storeBreakdown && storeBreakdown.stores && storeBreakdown.stores.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow">By Store</div>
              <div className="font-heading text-lg font-semibold tracking-tight">Store-wise split</div>
              <div className="text-[11.5px] text-muted-foreground mt-0.5">
                Consolidated total across all stores: <b className="text-foreground font-mono">{inr(totalStoreSales)}</b> sales
                · <b className="text-foreground font-mono">{inr(totalStoreOutstanding)}</b> outstanding
                · <b className="text-foreground font-mono">{inr(totalStoreTempPending)}</b> pending
              </div>
            </div>
            <Link to="/stores" className="text-[12px] text-primary hover:underline" data-testid="dash-stores-link">Manage stores →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* First card: consolidated total across every store */}
            <div className="card-flat p-4 flex flex-col gap-2 border-2 border-primary/40 bg-primary/5"
                 data-testid="dash-store-total">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-1.5">
                    <Storefront size={16} weight="fill" className="text-primary" />
                    <div className="font-heading text-[15px] font-semibold tracking-tight text-primary">
                      Total · All Stores
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-[11.5px] text-muted-foreground mt-0.5">
                    <MapPin size={11} weight="regular" /> {storeCount} store{storeCount > 1 ? "s" : ""} combined
                  </div>
                </div>
                <span className="pill pill-info text-[10px]">CONSOLIDATED</span>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-1">
                <div>
                  <div className="eyebrow text-[9.5px]">Sales</div>
                  <div className="font-mono font-semibold text-[13px]">{inr(totalStoreSales)}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {storeBreakdown.stores.reduce((s, x) => s + (x.invoice_count || 0), 0)} inv
                  </div>
                </div>
                <div>
                  <div className="eyebrow text-[9.5px]">Outstanding</div>
                  <div className={`font-mono font-semibold text-[13px] ${totalStoreOutstanding > 0 ? "text-destructive" : ""}`}>
                    {inr(totalStoreOutstanding)}
                  </div>
                  <div className="text-[10px] text-muted-foreground">Receivable</div>
                </div>
                <div>
                  <div className="eyebrow text-[9.5px]">Pending</div>
                  <div className="font-mono font-semibold text-[13px] text-amber-600">{inr(totalStoreTempPending)}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {storeBreakdown.stores.reduce((s, x) => s + (x.temp_count || 0), 0)} temps
                  </div>
                </div>
              </div>
              <div className="text-[10.5px] text-muted-foreground border-t border-primary/30 pt-2 mt-1">
                {storeBreakdown.stores.reduce((s, x) => s + (x.employee_count || 0), 0)} total staff assigned
              </div>
            </div>

            {storeBreakdown.stores.map((s) => (
              <div key={s.id} className="card-flat p-4 flex flex-col gap-2" data-testid={`dash-store-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <Storefront size={16} className="text-primary" />
                      <div className="font-heading text-[15px] font-semibold tracking-tight">{s.name}</div>
                    </div>
                    <div className="flex items-center gap-1 text-[11.5px] text-muted-foreground mt-0.5">
                      <MapPin size={11} weight="regular" /> {s.city}
                    </div>
                  </div>
                  {!s.active && <span className="pill pill-muted text-[10px]">INACTIVE</span>}
                </div>
                <div className="grid grid-cols-3 gap-2 pt-1">
                  <div>
                    <div className="eyebrow text-[9.5px]">Sales</div>
                    <div className="font-mono font-semibold text-[13px]">{inr(s.total_sales)}</div>
                    <div className="text-[10px] text-muted-foreground">{s.invoice_count} inv</div>
                  </div>
                  <div>
                    <div className="eyebrow text-[9.5px]">Outstanding</div>
                    <div className={`font-mono font-semibold text-[13px] ${s.outstanding > 0 ? "text-destructive" : ""}`}>{inr(s.outstanding)}</div>
                    <div className="text-[10px] text-muted-foreground">Receivable</div>
                  </div>
                  <div>
                    <div className="eyebrow text-[9.5px]">Pending</div>
                    <div className="font-mono font-semibold text-[13px] text-amber-600">{inr(s.temp_pending)}</div>
                    <div className="text-[10px] text-muted-foreground">{s.temp_count} temps</div>
                  </div>
                </div>
                <div className="text-[10.5px] text-muted-foreground border-t border-border pt-2 mt-1">
                  {s.employee_count} staff assigned
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cash & Bank live balances */}
      {cash && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow">Cash & Bank</div>
              <div className="font-heading text-lg font-semibold tracking-tight">Live wallet balances</div>
            </div>
            <Link to="/cashbook" className="text-[12px] text-primary hover:underline" data-testid="dash-cashbook-link">Open Cash Book →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="kpi" data-testid="dash-cash-in-hand">
              <div className="eyebrow flex items-center gap-1.5"><Vault size={12} /> Cash in Hand</div>
              <div className="val" style={{ color: (cash.balances.cash < 0) ? "hsl(var(--destructive))" : "hsl(var(--success))" }}>{inr(cash.balances.cash)}</div>
              <div className="text-[11px] font-mono mt-1 text-muted-foreground">Received this month: {inr(cash.totals.cash_in || 0)}</div>
            </div>
            <div className="kpi" data-testid="dash-bank-balance">
              <div className="eyebrow flex items-center gap-1.5"><Bank size={12} /> Bank Balance</div>
              <div className="val" style={{ color: (cash.balances.bank < 0) ? "hsl(var(--destructive))" : undefined }}>{inr(cash.balances.bank)}</div>
              <div className="text-[11px] font-mono mt-1 text-muted-foreground">Deposited this month: {inr(cash.totals.deposited_to_bank || 0)}</div>
            </div>
            <div className="kpi" data-testid="dash-total-liquidity">
              <div className="eyebrow">Total Liquidity</div>
              <div className="val">{inr(cash.balances.total)}</div>
              <div className="text-[11px] font-mono mt-1 text-muted-foreground">Cash + Bank (incl. UPI)</div>
            </div>
          </div>
        </div>
      )}

      {/* Chart + Low Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-flat p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow">Revenue vs Cost</div>
              <div className="font-heading text-lg font-semibold tracking-tight mt-0.5">6-Month Trend</div>
            </div>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground uppercase tracking-wider">
              <TrendUp size={14} /> Rolling 6 months
            </div>
          </div>
          <div className="h-72" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} stroke="hsl(var(--border))" />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} stroke="hsl(var(--border))" tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => inr(v)}
                />
                <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }} />
                <Bar dataKey="sales" fill="hsl(var(--chart-1))" name="Sales" radius={[3, 3, 0, 0]} />
                <Bar dataKey="purchases" fill="hsl(var(--chart-3))" name="Purchases" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow">Alerts</div>
              <div className="font-heading text-lg font-semibold tracking-tight mt-0.5">Low Stock</div>
            </div>
            <WarningCircle size={18} className="text-warning" weight="fill" style={{ color: "hsl(var(--warning))" }} />
          </div>
          {data.low_stock.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              All good. Nothing below reorder level.
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-border">
              {data.low_stock.map((p) => (
                <div key={p.id} className="py-2.5 flex items-center justify-between" data-testid={`low-stock-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium truncate">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5">Reorder ≤ {p.reorder_level}</div>
                  </div>
                  <span className="pill pill-warning">{p.current_stock}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Invoices */}
      <div className="card-flat">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="eyebrow">Latest</div>
            <div className="font-heading text-lg font-semibold tracking-tight mt-0.5">Recent Sales Invoices</div>
          </div>
          <Link to="/sales" className="text-[12px] font-medium text-primary hover:underline" data-testid="view-all-sales">
            View all →
          </Link>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Customer</th>
              <th>Date</th>
              <th className="num">Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.recent_invoices.length === 0 ? (
              <tr><td colSpan={5} className="text-center text-muted-foreground py-8">No invoices yet. Create your first invoice.</td></tr>
            ) : data.recent_invoices.map((r) => (
              <tr key={r.id} data-testid={`recent-inv-${r.id}`}>
                <td className="font-mono text-[12.5px]">{r.number}</td>
                <td>{r.customer_name}</td>
                <td className="text-muted-foreground">{fmtDate(r.date)}</td>
                <td className="num">{inr(r.grand_total)}</td>
                <td>
                  <span className={`pill ${r.status === "paid" ? "pill-success" : r.status === "partial" ? "pill-warning" : "pill-info"}`}>{r.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
